import React from 'react';
import { Button, Stack, Tooltip, useTheme } from '@mui/material';
import { isFuture } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';

interface AlertTooltipProps {
  tooltipContent: React.ReactNode;
  children: React.ReactElement;
  autoOpen?: boolean;
  autoOpenUntilDate?: Date;
  storageKey?: string;
}

function AlertTooltip({
  children,
  tooltipContent,
  autoOpen = true,
  autoOpenUntilDate,
  storageKey,
}: AlertTooltipProps) {
  const isClosed = !!storageKey && localStorage.getItem(storageKey) === 'true';
  const isDefaultOpen =
    !isClosed &&
    autoOpen &&
    (!autoOpenUntilDate || isFuture(autoOpenUntilDate));

  const [isOpen, setIsOpen] = React.useState(isDefaultOpen);

  const theme = useTheme();

  const onClose = React.useCallback(() => {
    setIsOpen(false);
    if (storageKey) {
      localStorage.setItem(storageKey, 'true');
    }
  }, [storageKey]);

  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target instanceof HTMLAnchorElement) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Tooltip
      title={
        <Stack position="relative" px={2} py={1.5}>
          {tooltipContent}
          <Button
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              p: 0.75,
              top: 0,
              right: 0,
              zIndex: 1000,
              color: 'common.white',
              minWidth: 'auto',
              width: 'auto',
              height: 'auto',
            }}>
            <CloseIcon sx={{ fontSize: '12px' }} />
          </Button>
        </Stack>
      }
      disableHoverListener
      open={isOpen}
      onClose={onClose}
      PopperProps={{
        onClick,
        sx: {
          '& .MuiTooltip-tooltip': {
            p: 0,
            backgroundColor: theme.palette.primary.main,
            boxShadow: theme.shadows[2],
          },
          '& .MuiTooltip-arrow': {
            color: theme.palette.primary.main,
          },
        },
      }}
      placement="top"
      onClick={onClose}
      arrow>
      {children}
    </Tooltip>
  );
}

export default AlertTooltip;
